import { LockOutlined } from "@ant-design/icons";
import type { AIConnector } from "@p0security/shared/types/ai-inventory";
import { getConnectorDisplayName } from "@p0security/shared/types/ai-inventory";
import { Handle, type NodeProps, Position } from "@xyflow/react";
import { Button } from "antd";
import { memo, useCallback } from "react";
import { color, font, numericStyle, radius } from "theme/aiInventory";

import { LiveDot } from "./LiveDot.tsx";
import { connectorIconFor } from "./connectorIcon.tsx";

export type ConnectorNodeData = {
  connector: AIConnector;
  /** Routes through the P0 MCP gateway. */
  managed: boolean;
  toolCount: number;
  expanded: boolean;
};

const baseStyle: React.CSSProperties = {
  borderRadius: radius.card,
  padding: "8px 10px 8px 14px",
  fontFamily: font.sans,
  fontSize: 12,
  width: 260,
  textAlign: "left",
  cursor: "pointer",
  position: "relative",
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const managedStyle: React.CSSProperties = {
  ...baseStyle,
  background: color.integrationBg,
  border: `1px solid ${color.integration}`,
  color: color.ink,
};

const unmanagedStyle: React.CSSProperties = {
  ...baseStyle,
  background: color.card,
  border: `1px dashed ${color.unmanaged}`,
  color: color.ink,
};

const kicker = (managed: boolean): React.CSSProperties => ({
  fontFamily: font.mono,
  fontSize: 9,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: managed ? color.integration : color.unmanaged,
  marginBottom: 1,
});

const countPill = (managed: boolean): React.CSSProperties => ({
  ...numericStyle,
  padding: "2px 6px",
  borderRadius: radius.sharp,
  fontSize: 10,
  background: managed ? color.integrationBgDeep : color.mute,
  color: color.inkDim,
});

const iconBadge = (managed: boolean): React.CSSProperties => ({
  width: 24,
  height: 24,
  borderRadius: "50%",
  background: managed ? color.integrationBgDeep : color.mute,
  color: managed ? color.integration : color.unmanaged,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 13,
  flexShrink: 0,
});

export const ConnectorNode: React.FC<NodeProps> = memo(({ data }) => {
  const typed = data as ConnectorNodeData & {
    onSecure?: (connector: AIConnector) => void;
  };
  const displayName = getConnectorDisplayName(typed.connector);
  const style = typed.managed ? managedStyle : unmanagedStyle;

  const handleSecure = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      typed.onSecure?.(typed.connector);
    },
    [typed]
  );

  const showSecure = !typed.managed && !!typed.onSecure;
  const containerStyle: React.CSSProperties = showSecure
    ? { ...style, flexDirection: "column", alignItems: "stretch", gap: 8 }
    : style;

  return (
    <div style={containerStyle} data-testid="connector-node">
      <Handle type="target" position={Position.Left} />
      <div
        style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}
      >
        {typed.managed && <LiveDot label="Live session" />}
        <span style={iconBadge(typed.managed)} aria-hidden>
          {connectorIconFor(displayName)}
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={kicker(typed.managed)}>
            {typed.managed ? "Managed" : "Unmanaged"}
          </div>
          <div
            style={{
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {displayName}
          </div>
        </div>
        {typed.toolCount > 0 && (
          <span style={{ ...countPill(typed.managed), marginLeft: "auto" }}>
            {typed.toolCount} tools
          </span>
        )}
      </div>
      {showSecure && (
        <Button
          type="primary"
          size="small"
          icon={<LockOutlined />}
          onClick={handleSecure}
          style={{ alignSelf: "flex-start" }}
        >
          Secure this connector
        </Button>
      )}
      <Handle type="source" position={Position.Right} />
    </div>
  );
});
ConnectorNode.displayName = "ConnectorNode";
